/**
 * Voice AI — Calls Filter Bar
 * Search input (debounced) + status/date/direction chip filters + active filter badges.
 * Dispatches events up to calls-page orchestrator.
 */

import { LitElement, html } from 'lit';
import { designSystem } from '../../styles/design-system.js';
import { callsStyles } from '../../styles/calls-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';

// Status filter options: UI label → DB value
const STATUS_OPTIONS = [
  { label: 'All',         value: 'all' },
  { label: 'Completed',   value: 'completed' },
  { label: 'Missed',      value: 'failed' },
  { label: 'Abandoned',   value: 'abandoned' },
  { label: 'Transferred', value: 'transferred' },
];

export class CallsFilterBar extends LitElement {
  static properties = {
    searchQuery:         { type: String },
    activeStatusFilters: { type: Array },
    activeDateRange:     { type: String },
    activeDirection:     { type: String },
  };

  static styles = [designSystem, callsStyles];

  constructor() {
    super();
    this.searchQuery = '';
    this.activeStatusFilters = [];
    this.activeDateRange = 'all';
    this.activeDirection = 'all';
    this._searchTimer = null;
  }

  // ── Handlers ──

  _onSearch(e) {
    this.searchQuery = e.target.value;
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this._emit('search', { query: this.searchQuery });
    }, 300);
  }

  _toggleStatus(option) {
    // "All" clears all status filters
    if (option.value === 'all') {
      this.activeStatusFilters = [];
      this._emit('filter-status', { statuses: [] });
      return;
    }

    const idx = this.activeStatusFilters.indexOf(option.value);
    let next;
    if (idx >= 0) {
      next = this.activeStatusFilters.filter(s => s !== option.value);
    } else {
      next = [...this.activeStatusFilters, option.value];
    }
    this.activeStatusFilters = next;
    this._emit('filter-status', { statuses: next });
  }

  _setDateRange(range) {
    this.activeDateRange = this.activeDateRange === range ? 'all' : range;
    this._emit('filter-date', { range: this.activeDateRange });
  }

  _setDirection(dir) {
    this.activeDirection = this.activeDirection === dir ? 'all' : dir;
    this._emit('filter-direction', { direction: this.activeDirection });
  }

  _dismissFilter(type, value) {
    switch (type) {
      case 'status':
        this.activeStatusFilters = this.activeStatusFilters.filter(s => s !== value);
        this._emit('filter-status', { statuses: this.activeStatusFilters });
        break;
      case 'date':
        this.activeDateRange = 'all';
        this._emit('filter-date', { range: 'all' });
        break;
      case 'direction':
        this.activeDirection = 'all';
        this._emit('filter-direction', { direction: 'all' });
        break;
    }
  }

  _emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  // ── Computed ──

  get _hasActiveFilters() {
    return this.activeStatusFilters.length > 0
      || this.activeDateRange !== 'all'
      || this.activeDirection !== 'all';
  }

  // ── Render ──

  render() {
    return html`
      ${iconStyles}
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Call History</h1>
          <p class="page-description">Monitor and review all voice AI call activity</p>
        </div>
      </div>

      <div class="filter-row">
        <div class="search-wrapper">
          <i class="fa-sharp fa-light fa-magnifying-glass search-icon"></i>
          <input
            type="text"
            placeholder="Search by phone, agent, or call SID..."
            .value="${this.searchQuery}"
            @input="${this._onSearch}"
          />
        </div>

        <div class="filter-group">
          <span class="filter-group-label">Status</span>
          ${STATUS_OPTIONS.map(opt => html`
            <span
              class="chip ${opt.value === 'all'
                ? (this.activeStatusFilters.length === 0 ? 'active' : '')
                : (this.activeStatusFilters.includes(opt.value) ? 'active' : '')}"
              @click="${() => this._toggleStatus(opt)}"
            >${opt.label}</span>
          `)}
        </div>

        <div class="filter-group">
          <span class="filter-group-label">Range</span>
          ${[['today', 'Today'], ['7d', '7D'], ['30d', '30D'], ['all', 'All']].map(([v, l]) => html`
            <span
              class="chip ${this.activeDateRange === v ? 'active' : ''}"
              @click="${() => this._setDateRange(v)}"
            >${l}</span>
          `)}
        </div>

        <div class="filter-group">
          <span class="filter-group-label">Direction</span>
          ${[['inbound', 'Inbound'], ['outbound', 'Outbound']].map(([v, l]) => html`
            <span
              class="chip ${this.activeDirection === v ? 'active' : ''}"
              @click="${() => this._setDirection(v)}"
            >${l}</span>
          `)}
        </div>
      </div>

      ${this._hasActiveFilters ? html`
        <div class="active-filters" style="margin-top: var(--space-2);">
          ${this.activeStatusFilters.map(s => {
            const opt = STATUS_OPTIONS.find(o => o.value === s);
            const label = opt ? opt.label : (s.charAt(0).toUpperCase() + s.slice(1));
            return html`
              <span class="filter-badge">
                ${label}
                <button class="dismiss" @click="${() => this._dismissFilter('status', s)}">
                  <i class="fa-sharp fa-light fa-xmark"></i>
                </button>
              </span>
            `;
          })}
          ${this.activeDateRange !== 'all' ? html`
            <span class="filter-badge">
              ${this.activeDateRange === 'today' ? 'Today' : this.activeDateRange === '7d' ? 'Last 7 days' : 'Last 30 days'}
              <button class="dismiss" @click="${() => this._dismissFilter('date')}">
                <i class="fa-sharp fa-light fa-xmark"></i>
              </button>
            </span>
          ` : ''}
          ${this.activeDirection !== 'all' ? html`
            <span class="filter-badge">
              ${this.activeDirection === 'inbound' ? 'Inbound' : 'Outbound'}
              <button class="dismiss" @click="${() => this._dismissFilter('direction')}">
                <i class="fa-sharp fa-light fa-xmark"></i>
              </button>
            </span>
          ` : ''}
        </div>
      ` : ''}
    `;
  }
}

customElements.define('calls-filter-bar', CallsFilterBar);
